import React, { useState } from 'react';
import { MapPin, Navigation, Copy, Upload, Download, Edit, Trash2, Plus, AlertTriangle, User, Lock, LogOut, Search, Route, Shield } from 'lucide-react';

const ORIGIN = { lat: 10.8199, lng: 77.1248, name: 'Coimbatore LPG BP' };

const styles = {
  loginContainer: { minHeight: '100vh', background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  loginCard: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', width: '100%', maxWidth: '400px', padding: '32px' },
  logoContainer: { textAlign: 'center', marginBottom: '32px' },
  logo: { margin: '0 auto', width: '80px', height: '80px', backgroundColor: '#ea580c', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' },
  title: { fontSize: '24px', fontWeight: 'bold', color: '#1f2937', margin: 0 },
  subtitle: { color: '#6b7280', marginTop: '8px', margin: 0 },
  tabContainer: { display: 'flex', borderBottom: '1px solid #d1d5db', marginBottom: '24px' },
  tab: { flex: 1, padding: '8px 16px', fontWeight: '500', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', borderBottom: '2px solid transparent' },
  activeDriverTab: { borderBottomColor: '#3b82f6', color: '#3b82f6' },
  activeAdminTab: { borderBottomColor: '#ea580c', color: '#ea580c' },
  inactiveTab: { color: '#6b7280' },
  inputContainer: { position: 'relative', marginBottom: '16px' },
  input: { width: '100%', paddingLeft: '40px', paddingRight: '16px', paddingTop: '12px', paddingBottom: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '16px', outline: 'none', boxSizing: 'border-box' },
  icon: { position: 'absolute', left: '12px', top: '12px', color: '#9ca3af' },
  button: { width: '100%', padding: '12px 16px', borderRadius: '8px', color: 'white', fontWeight: '500', border: 'none', cursor: 'pointer', fontSize: '16px' },
  driverButton: { backgroundColor: '#3b82f6' },
  adminButton: { backgroundColor: '#ea580c' },
  credentialsText: { marginTop: '24px', fontSize: '12px', color: '#6b7280', textAlign: 'center' },
  driverHeader: { backgroundColor: '#3b82f6', color: 'white', padding: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' },
  adminHeader: { backgroundColor: '#ea580c', color: 'white', padding: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' },
  headerContent: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerTitle: { fontSize: '20px', fontWeight: 'bold', margin: 0 },
  headerSubtitle: { opacity: 0.8, margin: 0 },
  logoutButton: { display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(255,255,255,0.2)', padding: '8px 16px', borderRadius: '8px', border: 'none', color: 'white', cursor: 'pointer' },
  mainContent: { minHeight: '100vh', backgroundColor: '#f9fafb' },
  card: { backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', padding: '24px', margin: '16px' },
  cardTitle: { fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' },
  th: { padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' },
  td: { padding: '12px', borderBottom: '1px solid #e5e7eb' },
  smallBtn: { border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px', color: 'white' }
};

const ADMIN_USER = '4179';
const ADMIN_PASS = '4100';
const DRIVER_USER = 'INDCBEBP';
const DRIVER_PASS = 'IOCLJRM';

// ---------- Hazard route diagram (red markers, drawn in-app) ----------
function RouteMap({ customer, hazards }) {
  const points = [
    { ...ORIGIN, type: 'origin' },
    ...hazards.map(h => ({ lat: h.lat, lng: h.lng, type: 'hazard', hazard: h })),
    { lat: customer.lat, lng: customer.lng, name: customer.name, type: 'destination' }
  ];
  const lats = points.map(p => p.lat);
  const lngs = points.map(p => p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const pad = 30, w = 400, h = 260;
  const scaleX = (lng) => {
    if (maxLng === minLng) return w / 2;
    return pad + ((lng - minLng) / (maxLng - minLng)) * (w - 2 * pad);
  };
  const scaleY = (lat) => {
    if (maxLat === minLat) return h / 2;
    return h - pad - ((lat - minLat) / (maxLat - minLat)) * (h - 2 * pad);
  };
  const originX = scaleX(ORIGIN.lng), originY = scaleY(ORIGIN.lat);
  const destX = scaleX(customer.lng), destY = scaleY(customer.lat);

  return (
    <svg viewBox={'0 0 ' + w + ' ' + h} style={{ width: '100%', maxWidth: '500px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #d1fae5' }}>
      <line x1={originX} y1={originY} x2={destX} y2={destY} stroke="#3b82f6" strokeWidth="2" strokeDasharray="6,4" />
      <circle cx={originX} cy={originY} r="8" fill="#16a34a" stroke="white" strokeWidth="2" />
      <text x={originX} y={originY - 12} fontSize="10" textAnchor="middle" fill="#166534">Origin</text>
      <circle cx={destX} cy={destY} r="8" fill="#2563eb" stroke="white" strokeWidth="2" />
      <text x={destX} y={destY - 12} fontSize="10" textAnchor="middle" fill="#1e40af">{customer.code}</text>
      {hazards.map((haz, i) => {
        const x = scaleX(haz.lng), y = scaleY(haz.lat);
        return (
          <g key={haz.id}>
            <circle cx={x} cy={y} r="7" fill="#dc2626" stroke="white" strokeWidth="2">
              <title>{haz.riskType + ' (' + haz.riskLevel + ') - Max ' + haz.speed + ' km/h'}</title>
            </circle>
            <text x={x} y={y + 20} fontSize="9" textAnchor="middle" fill="#991b1b">{i + 1}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ---------- Login screen ----------
function LoginScreen({ currentView, setCurrentView, loginCredentials, setLoginCredentials, handleDriverLogin, handleAdminLogin }) {
  return (
    <div style={styles.loginContainer}>
      <div style={styles.loginCard}>
        <div style={styles.logoContainer}>
          <div style={styles.logo}><Navigation size={40} color="white" /></div>
          <h1 style={styles.title}>IOCL LPG Navigation</h1>
          <p style={styles.subtitle}>Safe Route Management System</p>
        </div>

        <div style={styles.tabContainer}>
          <button onClick={() => setCurrentView('driver')} style={{ ...styles.tab, ...(currentView === 'driver' ? styles.activeDriverTab : styles.inactiveTab) }}>Driver Login</button>
          <button onClick={() => setCurrentView('admin')} style={{ ...styles.tab, ...(currentView === 'admin' ? styles.activeAdminTab : styles.inactiveTab) }}>Admin Login</button>
        </div>

        <div>
          <div style={styles.inputContainer}>
            <User style={styles.icon} size={20} />
            <input
              type="text"
              placeholder="Username"
              style={styles.input}
              value={loginCredentials.username}
              onChange={(e) => setLoginCredentials(prev => ({ ...prev, username: e.target.value }))}
            />
          </div>
          <div style={styles.inputContainer}>
            <Lock style={styles.icon} size={20} />
            <input
              type="password"
              placeholder="Password"
              style={styles.input}
              value={loginCredentials.password}
              onChange={(e) => setLoginCredentials(prev => ({ ...prev, password: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && (currentView === 'driver' ? handleDriverLogin() : handleAdminLogin())}
            />
          </div>
          <button
            onClick={currentView === 'driver' ? handleDriverLogin : handleAdminLogin}
            style={{ ...styles.button, ...(currentView === 'driver' ? styles.driverButton : styles.adminButton) }}
          >
            {currentView === 'driver' ? 'Driver Login' : 'Admin Login'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Driver interface ----------
function DriverInterface({ handleLogout, searchCustomerCode, setSearchCustomerCode, searchRoute, selectedRoute, showRouteDetails, openGoogleMaps, startNavigation, copyRouteInfo }) {
  return (
    <div style={styles.mainContent}>
      <div style={styles.driverHeader}>
        <div style={styles.headerContent}>
          <div>
            <h1 style={styles.headerTitle}>IOCL LPG Driver Navigation</h1>
            <p style={styles.headerSubtitle}>Coimbatore LPG BP - Safe Route System</p>
          </div>
          <button onClick={handleLogout} style={styles.logoutButton}><LogOut size={16} /><span>Logout</span></button>
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.cardTitle}><Search size={20} />Search Destination Route</h2>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Enter Customer Code (e.g., CUST001)"
            style={{ ...styles.input, flex: 1, paddingLeft: '16px' }}
            value={searchCustomerCode}
            onChange={(e) => setSearchCustomerCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchRoute()}
          />
          <button onClick={searchRoute} style={{ ...styles.button, ...styles.driverButton, width: 'auto', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} />Search Route
          </button>
        </div>

        {showRouteDetails && selectedRoute && (
          <div style={{ marginTop: '24px' }}>
            <h3 style={styles.cardTitle}><Route size={20} />Route Details</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div style={{ backgroundColor: '#f0fdf4', padding: '16px', borderRadius: '8px' }}>
                <h4 style={{ color: '#166534', margin: '0 0 8px 0' }}>Origin</h4>
                <p style={{ color: '#15803d', margin: 0 }}>{ORIGIN.name}</p>
                <p style={{ color: '#22c55e', fontSize: '14px', margin: 0 }}>{ORIGIN.lat}, {ORIGIN.lng}</p>
              </div>
              <div style={{ backgroundColor: '#eff6ff', padding: '16px', borderRadius: '8px' }}>
                <h4 style={{ color: '#1e40af', margin: '0 0 8px 0' }}>Destination</h4>
                <p style={{ color: '#2563eb', margin: 0 }}>{selectedRoute.customer.name}</p>
                <p style={{ color: '#3b82f6', fontSize: '14px', margin: 0 }}>Code: {selectedRoute.customer.code}</p>
                <p style={{ color: '#3b82f6', fontSize: '14px', margin: 0 }}>{selectedRoute.customer.lat}, {selectedRoute.customer.lng}</p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <button onClick={openGoogleMaps} style={{ backgroundColor: '#16a34a', color: 'white', padding: '12px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <MapPin size={16} />Open Google Maps
              </button>
              <button onClick={startNavigation} style={{ backgroundColor: '#3b82f6', color: 'white', padding: '12px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Navigation size={16} />Start Navigation
              </button>
              <button onClick={copyRouteInfo} style={{ backgroundColor: '#ea580c', color: 'white', padding: '12px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Copy size={16} />Copy Route Info
              </button>
            </div>

            {selectedRoute.hazards.length > 0 && (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ color: '#374151', margin: '0 0 8px 0', fontSize: '15px', fontWeight: '600' }}>Route Map (hazards shown in red)</h3>
                  <RouteMap customer={selectedRoute.customer} hazards={selectedRoute.hazards} />
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                    Note: Google Maps links below include hazard stops as waypoints, but Google only labels them A/B/C — this diagram is the clearest way to see hazard positions before you drive.
                  </p>
                </div>

                <div style={{ backgroundColor: '#fef2f2', padding: '16px', borderRadius: '8px' }}>
                  <h3 style={{ color: '#991b1b', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AlertTriangle size={20} />Hazards on Route ({selectedRoute.hazards.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {selectedRoute.hazards.map((hazard, index) => (
                      <div key={hazard.id} style={{ backgroundColor: 'white', padding: '12px', borderRadius: '6px', borderLeft: '4px solid #dc2626' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <p style={{ fontWeight: '600', color: '#991b1b', margin: '0 0 4px 0' }}>{index + 1}. {hazard.riskType}</p>
                            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>Risk Level: <span style={{ fontWeight: '500' }}>{hazard.riskLevel}</span></p>
                            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Speed Limit: <span style={{ fontWeight: '500' }}>{hazard.speed} km/h</span></p>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px 0' }}>Location</p>
                            <p style={{ fontSize: '14px', fontFamily: 'monospace', margin: 0 }}>{hazard.lat}, {hazard.lng}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Admin interface ----------
function AdminInterface(props) {
  const {
    handleLogout, customers, hazards,
    newCustomer, setNewCustomer, addCustomer, editingCustomer, setEditingCustomer, updateCustomer, deleteCustomer,
    newHazard, setNewHazard, addHazard, editingHazard, setEditingHazard, updateHazard, deleteHazard,
    csvFile, uploadStatus, uploadProgress, selectedCustomerForUpload, setSelectedCustomerForUpload, handleCSVFileSelect, processCSVUpload, downloadSampleCSV
  } = props;

  return (
    <div style={styles.mainContent}>
      <div style={styles.adminHeader}>
        <div style={styles.headerContent}>
          <div>
            <h1 style={styles.headerTitle}>IOCL Admin Panel</h1>
            <p style={styles.headerSubtitle}>Route & Hazard Management System</p>
          </div>
          <button onClick={handleLogout} style={styles.logoutButton}><LogOut size={16} /><span>Logout</span></button>
        </div>
      </div>

      {/* Customer Management */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Customer Management</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '16px' }}>
          <input type="text" placeholder="Customer Code" style={{ ...styles.input, paddingLeft: '16px' }} value={newCustomer.code} onChange={(e) => setNewCustomer(prev => ({ ...prev, code: e.target.value }))} />
          <input type="text" placeholder="Customer Name" style={{ ...styles.input, paddingLeft: '16px' }} value={newCustomer.name} onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))} />
          <input type="number" placeholder="Latitude" step="0.0001" style={{ ...styles.input, paddingLeft: '16px' }} value={newCustomer.lat} onChange={(e) => setNewCustomer(prev => ({ ...prev, lat: e.target.value }))} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <input type="number" placeholder="Longitude" step="0.0001" style={{ ...styles.input, paddingLeft: '16px', flex: 1 }} value={newCustomer.lng} onChange={(e) => setNewCustomer(prev => ({ ...prev, lng: e.target.value }))} />
            <button onClick={addCustomer} style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', padding: '12px', cursor: 'pointer' }}><Plus size={16} /></button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={styles.th}>Code</th><th style={styles.th}>Name</th><th style={styles.th}>Latitude</th><th style={styles.th}>Longitude</th><th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(customer => {
                const isEditing = editingCustomer && editingCustomer.id === customer.id;
                return (
                  <tr key={customer.id}>
                    {isEditing ? (
                      <>
                        <td style={styles.td}><input style={{ width: '100%', padding: '4px' }} value={editingCustomer.code} onChange={(e) => setEditingCustomer(prev => ({ ...prev, code: e.target.value }))} /></td>
                        <td style={styles.td}><input style={{ width: '100%', padding: '4px' }} value={editingCustomer.name} onChange={(e) => setEditingCustomer(prev => ({ ...prev, name: e.target.value }))} /></td>
                        <td style={styles.td}><input type="number" step="0.0001" style={{ width: '100%', padding: '4px' }} value={editingCustomer.lat} onChange={(e) => setEditingCustomer(prev => ({ ...prev, lat: e.target.value }))} /></td>
                        <td style={styles.td}><input type="number" step="0.0001" style={{ width: '100%', padding: '4px' }} value={editingCustomer.lng} onChange={(e) => setEditingCustomer(prev => ({ ...prev, lng: e.target.value }))} /></td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={updateCustomer} style={{ ...styles.smallBtn, backgroundColor: '#16a34a' }}>Save</button>
                            <button onClick={() => setEditingCustomer(null)} style={{ ...styles.smallBtn, backgroundColor: '#6b7280' }}>Cancel</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ ...styles.td, fontWeight: '500' }}>{customer.code}</td>
                        <td style={styles.td}>{customer.name}</td>
                        <td style={styles.td}>{customer.lat}</td>
                        <td style={styles.td}>{customer.lng}</td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => setEditingCustomer({ ...customer })} style={{ ...styles.smallBtn, backgroundColor: '#3b82f6' }}><Edit size={12} /></button>
                            <button onClick={() => deleteCustomer(customer.id)} style={{ ...styles.smallBtn, backgroundColor: '#dc2626' }}><Trash2 size={12} /></button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* CSV Upload */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}><Upload size={20} />CSV Hazard Upload System</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Select Customer for Hazard Upload</label>
              <select style={{ ...styles.input, paddingLeft: '16px' }} value={selectedCustomerForUpload} onChange={(e) => setSelectedCustomerForUpload(e.target.value)}>
                <option value="">Choose Customer...</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.code} - {c.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Upload CSV File</label>
              <input type="file" accept=".csv" onChange={handleCSVFileSelect} style={{ ...styles.input, paddingLeft: '16px' }} />
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <button
                onClick={processCSVUpload}
                disabled={!csvFile || !selectedCustomerForUpload || uploadStatus === 'processing'}
                style={{ backgroundColor: (!csvFile || !selectedCustomerForUpload || uploadStatus === 'processing') ? '#9ca3af' : '#ea580c', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
              ><Upload size={16} />Upload CSV</button>
              <button onClick={downloadSampleCSV} style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}><Download size={16} />Sample CSV</button>
            </div>
          </div>
          <div>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Upload Status</h3>
              <div style={{
                padding: '12px', borderRadius: '8px',
                backgroundColor: uploadStatus === 'ready' ? '#dbeafe' : uploadStatus === 'processing' ? '#fef3c7' : uploadStatus === 'complete' ? '#dcfce7' : uploadStatus === 'error' ? '#fee2e2' : '#f9fafb',
                color: uploadStatus === 'ready' ? '#1e40af' : uploadStatus === 'processing' ? '#92400e' : uploadStatus === 'complete' ? '#166534' : uploadStatus === 'error' ? '#991b1b' : '#6b7280'
              }}>
                {uploadStatus === 'ready' && 'File ready for upload'}
                {uploadStatus === 'processing' && 'Processing file...'}
                {uploadStatus === 'complete' && 'Upload completed successfully!'}
                {uploadStatus === 'error' && 'Upload failed. Please check file format.'}
                {!uploadStatus && 'No file selected'}
              </div>
            </div>
            {uploadStatus === 'processing' && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ width: '100%', backgroundColor: '#e5e7eb', borderRadius: '9999px', height: '8px' }}>
                  <div style={{ backgroundColor: '#ea580c', height: '8px', borderRadius: '9999px', width: uploadProgress + '%' }}></div>
                </div>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>{Math.round(uploadProgress)}% Complete</p>
              </div>
            )}
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              <p style={{ fontWeight: '500', margin: 0 }}>CSV Format Required:</p>
              <p style={{ margin: 0 }}>Risk Type, Risk Level, Latitude, Longitude, Speed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Hazard Management */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}><Shield size={20} />Hazard Management</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: '16px' }}>
          <select style={{ ...styles.input, paddingLeft: '16px' }} value={newHazard.customerId} onChange={(e) => setNewHazard(prev => ({ ...prev, customerId: e.target.value }))}>
            <option value="">Select Customer</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
          </select>
          <input type="text" placeholder="Risk Type" style={{ ...styles.input, paddingLeft: '16px' }} value={newHazard.riskType} onChange={(e) => setNewHazard(prev => ({ ...prev, riskType: e.target.value }))} />
          <select style={{ ...styles.input, paddingLeft: '16px' }} value={newHazard.riskLevel} onChange={(e) => setNewHazard(prev => ({ ...prev, riskLevel: e.target.value }))}>
            <option value="">Risk Level</option><option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option>
          </select>
          <input type="number" placeholder="Latitude" step="0.0001" style={{ ...styles.input, paddingLeft: '16px' }} value={newHazard.lat} onChange={(e) => setNewHazard(prev => ({ ...prev, lat: e.target.value }))} />
          <input type="number" placeholder="Longitude" step="0.0001" style={{ ...styles.input, paddingLeft: '16px' }} value={newHazard.lng} onChange={(e) => setNewHazard(prev => ({ ...prev, lng: e.target.value }))} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <input type="number" placeholder="Speed (km/h)" style={{ ...styles.input, paddingLeft: '16px', flex: 1 }} value={newHazard.speed} onChange={(e) => setNewHazard(prev => ({ ...prev, speed: e.target.value }))} />
            <button onClick={addHazard} style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', padding: '12px', cursor: 'pointer' }}><Plus size={16} /></button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={styles.th}>Customer</th><th style={styles.th}>Risk Type</th><th style={styles.th}>Risk Level</th><th style={styles.th}>Location</th><th style={styles.th}>Speed</th><th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {hazards.map(hazard => {
                const customer = customers.find(c => c.id === hazard.customerId);
                const isEditing = editingHazard && editingHazard.id === hazard.id;
                return (
                  <tr key={hazard.id}>
                    {isEditing ? (
                      <>
                        <td style={styles.td}>
                          <select style={{ width: '100%', padding: '4px' }} value={editingHazard.customerId} onChange={(e) => setEditingHazard(prev => ({ ...prev, customerId: e.target.value }))}>
                            {customers.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
                          </select>
                        </td>
                        <td style={styles.td}><input style={{ width: '100%', padding: '4px' }} value={editingHazard.riskType} onChange={(e) => setEditingHazard(prev => ({ ...prev, riskType: e.target.value }))} /></td>
                        <td style={styles.td}>
                          <select style={{ width: '100%', padding: '4px' }} value={editingHazard.riskLevel} onChange={(e) => setEditingHazard(prev => ({ ...prev, riskLevel: e.target.value }))}>
                            <option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option>
                          </select>
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <input type="number" step="0.0001" style={{ width: '50%', padding: '4px' }} value={editingHazard.lat} onChange={(e) => setEditingHazard(prev => ({ ...prev, lat: e.target.value }))} />
                            <input type="number" step="0.0001" style={{ width: '50%', padding: '4px' }} value={editingHazard.lng} onChange={(e) => setEditingHazard(prev => ({ ...prev, lng: e.target.value }))} />
                          </div>
                        </td>
                        <td style={styles.td}><input type="number" style={{ width: '100%', padding: '4px' }} value={editingHazard.speed} onChange={(e) => setEditingHazard(prev => ({ ...prev, speed: e.target.value }))} /></td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={updateHazard} style={{ ...styles.smallBtn, backgroundColor: '#16a34a' }}>Save</button>
                            <button onClick={() => setEditingHazard(null)} style={{ ...styles.smallBtn, backgroundColor: '#6b7280' }}>Cancel</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ ...styles.td, fontWeight: '500' }}>{customer ? customer.code : 'Unknown'}</td>
                        <td style={styles.td}>{hazard.riskType}</td>
                        <td style={styles.td}>
                          <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500', backgroundColor: hazard.riskLevel === 'High' ? '#fee2e2' : hazard.riskLevel === 'Medium' ? '#fef3c7' : '#dcfce7', color: hazard.riskLevel === 'High' ? '#991b1b' : hazard.riskLevel === 'Medium' ? '#92400e' : '#166534' }}>{hazard.riskLevel}</span>
                        </td>
                        <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '12px' }}>{hazard.lat}, {hazard.lng}</td>
                        <td style={styles.td}>{hazard.speed} km/h</td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => setEditingHazard({ ...hazard })} style={{ ...styles.smallBtn, backgroundColor: '#3b82f6' }}><Edit size={12} /></button>
                            <button onClick={() => deleteHazard(hazard.id)} style={{ ...styles.smallBtn, backgroundColor: '#dc2626' }}><Trash2 size={12} /></button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------- Main app ----------
export default function IOCLDriverApp() {
  const [isDriverLoggedIn, setIsDriverLoggedIn] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [currentView, setCurrentView] = useState('driver');
  const [loginCredentials, setLoginCredentials] = useState({ username: '', password: '' });

  const [searchCustomerCode, setSearchCustomerCode] = useState('');
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showRouteDetails, setShowRouteDetails] = useState(false);

  const [customers, setCustomers] = useState([
    { id: 1, code: 'CUST001', name: 'ABC Industries', lat: 17.2000, lng: 78.3000 },
    { id: 2, code: 'CUST002', name: 'XYZ Corporation', lat: 17.1800, lng: 78.3200 },
    { id: 3, code: 'CUST003', name: 'PQR Limited', lat: 17.2200, lng: 78.2800 }
  ]);
  const [hazards, setHazards] = useState([
    { id: 1, customerId: 1, riskType: 'Sharp Turn', riskLevel: 'Medium', lat: 17.1700, lng: 78.2950, speed: 30 },
    { id: 2, customerId: 1, riskType: 'School Zone', riskLevel: 'High', lat: 17.1750, lng: 78.2980, speed: 20 },
    { id: 3, customerId: 2, riskType: 'Construction', riskLevel: 'High', lat: 17.1650, lng: 78.3100, speed: 25 }
  ]);

  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingHazard, setEditingHazard] = useState(null);
  const [newCustomer, setNewCustomer] = useState({ code: '', name: '', lat: '', lng: '' });
  const [newHazard, setNewHazard] = useState({ customerId: '', riskType: '', riskLevel: '', lat: '', lng: '', speed: '' });

  const [csvFile, setCsvFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedCustomerForUpload, setSelectedCustomerForUpload] = useState('');

  const handleDriverLogin = () => {
    if (loginCredentials.username === DRIVER_USER && loginCredentials.password === DRIVER_PASS) {
      setIsDriverLoggedIn(true);
      setLoginCredentials({ username: '', password: '' });
    } else {
      alert('Invalid credentials! Please check username and password.');
    }
  };

  const handleAdminLogin = () => {
    if (loginCredentials.username === ADMIN_USER && loginCredentials.password === ADMIN_PASS) {
      setIsAdminLoggedIn(true);
      setLoginCredentials({ username: '', password: '' });
    } else {
      alert('Invalid admin credentials!');
    }
  };

  const handleLogout = () => {
    setIsDriverLoggedIn(false);
    setIsAdminLoggedIn(false);
    setCurrentView('driver');
    setSelectedRoute(null);
    setShowRouteDetails(false);
  };

  const searchRoute = () => {
    const customer = customers.find(c => c.code.toLowerCase() === searchCustomerCode.toLowerCase());
    if (customer) {
      const routeHazards = hazards.filter(h => h.customerId === customer.id);
      setSelectedRoute({ customer, hazards: routeHazards });
      setShowRouteDetails(true);
    } else {
      alert('Customer code not found! Please check the code and try again.');
    }
  };

  const buildMapsUrl = (customer, hazardList, navMode) => {
    let url = 'https://www.google.com/maps/dir/' + ORIGIN.lat + ',' + ORIGIN.lng;
    hazardList.forEach(h => { url += '/' + h.lat + ',' + h.lng; });
    url += '/' + customer.lat + ',' + customer.lng;
    if (navMode) url += '/@' + customer.lat + ',' + customer.lng + ',15z/data=!4m2!4m1!3e0';
    return url;
  };

  const openGoogleMaps = () => {
    if (!selectedRoute) return;
    const { customer, hazards: routeHazards } = selectedRoute;
    const url = buildMapsUrl(customer, routeHazards, false);
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (!newWindow) alert('Route URL: ' + url + '\n\nPopup blocked - please copy this URL into your browser.');
  };

  const startNavigation = () => {
    if (!selectedRoute) return;
    const { customer, hazards: routeHazards } = selectedRoute;
    const url = buildMapsUrl(customer, routeHazards, true);
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (!newWindow) alert('Navigation URL: ' + url + '\n\nPopup blocked - please copy this URL into your browser.');
  };

  const copyRouteInfo = () => {
    if (!selectedRoute) return;
    const { customer, hazards: routeHazards } = selectedRoute;
    let info = 'IOCL LPG Route Information\n================================\n';
    info += 'Origin: ' + ORIGIN.name + ' (' + ORIGIN.lat + ', ' + ORIGIN.lng + ')\n';
    info += 'Destination: ' + customer.name + ' (' + customer.lat + ', ' + customer.lng + ')\n';
    info += 'Customer Code: ' + customer.code + '\n\n';
    info += 'Google Maps URL: ' + buildMapsUrl(customer, routeHazards, false) + '\n';
    info += 'Navigation URL: ' + buildMapsUrl(customer, routeHazards, true) + '\n\n';
    if (routeHazards.length > 0) {
      info += 'HAZARDS ON ROUTE:\n==================\n';
      routeHazards.forEach((h, i) => {
        info += (i + 1) + '. ' + h.riskType + ' | Risk: ' + h.riskLevel + ' | Location: ' + h.lat + ',' + h.lng + ' | Max speed: ' + h.speed + ' km/h\n';
      });
    }
    navigator.clipboard.writeText(info).then(() => {
      alert('Route information copied to clipboard!');
    }).catch(() => {
      alert('Could not copy automatically. Route info:\n\n' + info);
    });
  };

  const addCustomer = () => {
    if (newCustomer.code && newCustomer.name && newCustomer.lat && newCustomer.lng) {
      setCustomers(prev => [...prev, { id: Date.now(), code: newCustomer.code.toUpperCase(), name: newCustomer.name, lat: parseFloat(newCustomer.lat), lng: parseFloat(newCustomer.lng) }]);
      setNewCustomer({ code: '', name: '', lat: '', lng: '' });
    } else {
      alert('Please fill all customer fields!');
    }
  };

  const updateCustomer = () => {
    setCustomers(prev => prev.map(c => c.id === editingCustomer.id ? { ...editingCustomer, lat: parseFloat(editingCustomer.lat), lng: parseFloat(editingCustomer.lng) } : c));
    setEditingCustomer(null);
  };

  const deleteCustomer = (id) => {
    if (window.confirm('Delete this customer and its hazards?')) {
      setCustomers(prev => prev.filter(c => c.id !== id));
      setHazards(prev => prev.filter(h => h.customerId !== id));
    }
  };

  const addHazard = () => {
    if (newHazard.customerId && newHazard.riskType && newHazard.riskLevel && newHazard.lat && newHazard.lng && newHazard.speed) {
      setHazards(prev => [...prev, { id: Date.now(), customerId: parseInt(newHazard.customerId), riskType: newHazard.riskType, riskLevel: newHazard.riskLevel, lat: parseFloat(newHazard.lat), lng: parseFloat(newHazard.lng), speed: parseInt(newHazard.speed) }]);
      setNewHazard({ customerId: '', riskType: '', riskLevel: '', lat: '', lng: '', speed: '' });
    } else {
      alert('Please fill all hazard fields!');
    }
  };

  const updateHazard = () => {
    setHazards(prev => prev.map(h => h.id === editingHazard.id ? { ...editingHazard, customerId: parseInt(editingHazard.customerId), lat: parseFloat(editingHazard.lat), lng: parseFloat(editingHazard.lng), speed: parseInt(editingHazard.speed) } : h));
    setEditingHazard(null);
  };

  const deleteHazard = (id) => {
    if (window.confirm('Delete this hazard?')) setHazards(prev => prev.filter(h => h.id !== id));
  };

  const handleCSVFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setCsvFile(file);
      setUploadStatus('ready');
    } else {
      alert('Please select a valid CSV file');
    }
  };

  const processCSVUpload = () => {
    if (!csvFile || !selectedCustomerForUpload) {
      alert('Please select both a CSV file and a customer');
      return;
    }
    setUploadStatus('processing');
    setUploadProgress(0);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split('\n').filter(l => l.trim());
        const header = lines[0].toLowerCase();
        if (!header.includes('risk type') || !header.includes('risk level') || !header.includes('latitude') || !header.includes('longitude') || !header.includes('speed')) {
          throw new Error('CSV must contain columns: Risk Type, Risk Level, Latitude, Longitude, Speed');
        }
        const parsed = [];
        for (let i = 1; i < lines.length; i++) {
          const data = lines[i].split(',');
          if (data.length >= 5) {
            parsed.push({ id: Date.now() + i, customerId: parseInt(selectedCustomerForUpload), riskType: data[0].trim(), riskLevel: data[1].trim(), lat: parseFloat(data[2].trim()), lng: parseFloat(data[3].trim()), speed: parseInt(data[4].trim()) });
          }
          setUploadProgress((i / (lines.length - 1)) * 100);
        }
        setTimeout(() => {
          setHazards(prev => prev.filter(h => h.customerId !== parseInt(selectedCustomerForUpload)).concat(parsed));
          setUploadStatus('complete');
          setUploadProgress(100);
          setCsvFile(null);
          setSelectedCustomerForUpload('');
        }, 600);
      } catch (err) {
        setUploadStatus('error');
        alert('Error processing CSV: ' + err.message);
      }
    };
    reader.readAsText(csvFile);
  };

  const downloadSampleCSV = () => {
    const sample = 'Risk Type,Risk Level,Latitude,Longitude,Speed\nSharp Turn,Medium,17.1700,78.2950,30\nSchool Zone,High,17.1750,78.2980,20\nConstruction,High,17.1650,78.3100,25';
    const blob = new Blob([sample], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sample_hazards.csv';
    document.body.appendChild(a); a.click();
    window.URL.revokeObjectURL(url); document.body.removeChild(a);
  };

  if (isDriverLoggedIn) {
    return (
      <DriverInterface
        handleLogout={handleLogout}
        searchCustomerCode={searchCustomerCode}
        setSearchCustomerCode={setSearchCustomerCode}
        searchRoute={searchRoute}
        selectedRoute={selectedRoute}
        showRouteDetails={showRouteDetails}
        openGoogleMaps={openGoogleMaps}
        startNavigation={startNavigation}
        copyRouteInfo={copyRouteInfo}
      />
    );
  }

  if (isAdminLoggedIn) {
    return (
      <AdminInterface
        handleLogout={handleLogout}
        customers={customers}
        hazards={hazards}
        newCustomer={newCustomer} setNewCustomer={setNewCustomer} addCustomer={addCustomer}
        editingCustomer={editingCustomer} setEditingCustomer={setEditingCustomer} updateCustomer={updateCustomer} deleteCustomer={deleteCustomer}
        newHazard={newHazard} setNewHazard={setNewHazard} addHazard={addHazard}
        editingHazard={editingHazard} setEditingHazard={setEditingHazard} updateHazard={updateHazard} deleteHazard={deleteHazard}
        csvFile={csvFile} uploadStatus={uploadStatus} uploadProgress={uploadProgress}
        selectedCustomerForUpload={selectedCustomerForUpload} setSelectedCustomerForUpload={setSelectedCustomerForUpload}
        handleCSVFileSelect={handleCSVFileSelect} processCSVUpload={processCSVUpload} downloadSampleCSV={downloadSampleCSV}
      />
    );
  }

  return (
    <LoginScreen
      currentView={currentView}
      setCurrentView={setCurrentView}
      loginCredentials={loginCredentials}
      setLoginCredentials={setLoginCredentials}
      handleDriverLogin={handleDriverLogin}
      handleAdminLogin={handleAdminLogin}
    />
  );
}